import { useState } from 'react';
import { ChevronDown, ChevronRight } from 'lucide-react';

// --- Badge components ---

function DdbBadge({ table, mode }: { table: string; mode: 'read' | 'write' | 'read/write' }) {
  const colors = mode === 'read' ? 'bg-blue-100 text-blue-800' : mode === 'write' ? 'bg-blue-200 text-blue-900' : 'bg-blue-150 text-blue-850';
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-mono ${colors}`}>
      <span className="w-2 h-2 rounded-full bg-blue-500 inline-block" />
      {table} <span className="font-semibold">({mode})</span>
    </span>
  );
}

function ServiceBadge({ name, color }: { name: string; color: 'green' | 'purple' | 'orange' | 'gray' }) {
  const colorMap = {
    green: 'bg-green-100 text-green-800',
    purple: 'bg-purple-100 text-purple-800',
    orange: 'bg-orange-100 text-orange-800',
    gray: 'bg-gray-200 text-gray-700',
  };
  const dotMap = {
    green: 'bg-green-500',
    purple: 'bg-purple-500',
    orange: 'bg-orange-500',
    gray: 'bg-gray-500',
  };
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium ${colorMap[color]}`}>
      <span className={`w-2 h-2 rounded-full ${dotMap[color]} inline-block`} />
      {name}
    </span>
  );
}

function SyncBadge({ isAsync }: { isAsync: boolean }) {
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${isAsync ? 'bg-amber-100 text-amber-800' : 'bg-emerald-100 text-emerald-800'}`}>
      {isAsync ? 'Async' : 'Sync'}
    </span>
  );
}

// --- Step card ---

interface StepData {
  number: number;
  lambda: string;
  description: string;
  tables: { name: string; mode: 'read' | 'write' | 'read/write' }[];
  services: { name: string; color: 'green' | 'purple' | 'orange' | 'gray' }[];
  isAsync: boolean;
}

function StepCard({ step }: { step: StepData }) {
  return (
    <div className="bg-white border border-gray-200 rounded-xl p-4 shadow-sm hover:shadow-md transition-shadow">
      <div className="flex items-start gap-3">
        <div className="flex-shrink-0 w-8 h-8 rounded-full bg-gray-800 text-white flex items-center justify-center text-sm font-bold">
          {step.number}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap mb-1">
            <code className="text-sm font-semibold bg-gray-100 px-2 py-0.5 rounded text-gray-900">{step.lambda}</code>
            <SyncBadge isAsync={step.isAsync} />
          </div>
          <p className="text-sm text-gray-600 mb-2">{step.description}</p>
          {step.tables.length > 0 && (
            <div className="flex flex-wrap gap-1 mb-1">
              {step.tables.map((t, i) => <DdbBadge key={i} table={t.name} mode={t.mode} />)}
            </div>
          )}
          {step.services.length > 0 && (
            <div className="flex flex-wrap gap-1">
              {step.services.map((s, i) => <ServiceBadge key={i} name={s.name} color={s.color} />)}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function Arrow() {
  return (
    <div className="flex justify-center py-1">
      <div className="text-gray-400 text-lg">&#x2193;</div>
    </div>
  );
}

// --- Flow section ---

interface FlowSectionProps {
  title: string;
  subtitle: string;
  steps: StepData[];
  defaultOpen?: boolean;
}

function FlowSection({ title, subtitle, steps, defaultOpen = false }: FlowSectionProps) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between p-5 text-left hover:bg-gray-50 transition-colors"
      >
        <div>
          <h2 className="text-lg font-bold text-gray-800">{title}</h2>
          <p className="text-sm text-gray-500 mt-0.5">{subtitle}</p>
        </div>
        <div className="flex-shrink-0 ml-4">
          {open ? <ChevronDown className="w-5 h-5 text-gray-400" /> : <ChevronRight className="w-5 h-5 text-gray-400" />}
        </div>
      </button>
      {open && (
        <div className="px-5 pb-5 space-y-0">
          {steps.map((step, i) => (
            <div key={i}>
              {i > 0 && <Arrow />}
              <StepCard step={step} />
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// --- Color legend ---

function ColorLegend() {
  return (
    <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-4 mb-6">
      <h3 className="text-sm font-semibold text-gray-700 mb-2">Color Legend</h3>
      <div className="flex flex-wrap gap-3">
        <DdbBadge table="DynamoDB" mode="read" />
        <ServiceBadge name="S3" color="green" />
        <ServiceBadge name="Bedrock / AI" color="purple" />
        <ServiceBadge name="SQS" color="orange" />
        <ServiceBadge name="Lambda" color="gray" />
      </div>
    </div>
  );
}

// --- Data definitions ---

const resumeCreationSteps: StepData[] = [
  {
    number: 1,
    lambda: 'Frontend',
    description: 'User saves resume in ResumeEditor. Sends POST /resumes to API Gateway.',
    tables: [],
    services: [],
    isAsync: false,
  },
  {
    number: 2,
    lambda: 'skillsnap-resume-create',
    description: 'Validates resume payload and writes the resume JSON to DynamoDB.',
    tables: [{ name: 'skillsnap-resume', mode: 'write' }],
    services: [{ name: 'Lambda', color: 'gray' }],
    isAsync: false,
  },
  {
    number: 3,
    lambda: 'skillsnap-gentags (resume)',
    description: 'Calls Bedrock Claude with gen_tags_resume prompt to extract tags from resume items. Maps tags back to resume items and writes updated resumejson.',
    tables: [{ name: 'skillsnap-resume', mode: 'read/write' }],
    services: [{ name: 'Bedrock Claude', color: 'purple' }, { name: 'Lambda', color: 'gray' }],
    isAsync: true,
  },
  {
    number: 4,
    lambda: 'skillsnap-gentags (embed)',
    description: 'Calls Bedrock Titan to create vector embeddings for all unique tags. Stores embeddings for later tag-matching.',
    tables: [{ name: 'skillsnap-tag-embeddings', mode: 'write' }],
    services: [{ name: 'Bedrock Titan', color: 'purple' }, { name: 'Lambda', color: 'gray' }],
    isAsync: true,
  },
  {
    number: 5,
    lambda: 'skillsnap-genmasterresume',
    description: 'Reads resume data and user preferences, renders HTML master resume, uploads to S3, and invalidates CloudFront cache.',
    tables: [{ name: 'skillsnap-resume', mode: 'read' }, { name: 'skillsnap-user', mode: 'read' }],
    services: [{ name: 'S3', color: 'green' }, { name: 'CloudFront', color: 'green' }, { name: 'Lambda', color: 'gray' }],
    isAsync: true,
  },
];

const emailJobSteps: StepData[] = [
  {
    number: 1,
    lambda: 'SES',
    description: 'Incoming email received by SES. Raw email stored in S3 inbound bucket, which triggers the router Lambda.',
    tables: [],
    services: [{ name: 'SES', color: 'orange' }, { name: 'S3', color: 'green' }],
    isAsync: false,
  },
  {
    number: 2,
    lambda: 'skillsnap-job-router',
    description: 'Parses the inbound email, looks up the sender in the user-email table, and identifies the appropriate job board parser.',
    tables: [{ name: 'skillsnap-user-email', mode: 'read' }],
    services: [{ name: 'S3', color: 'green' }, { name: 'Lambda', color: 'gray' }],
    isAsync: false,
  },
  {
    number: 3,
    lambda: 'Parser Lambda',
    description: 'Extracts structured job data (title, company, description, URL) from the email HTML using board-specific parsing logic.',
    tables: [],
    services: [{ name: 'Lambda', color: 'gray' }],
    isAsync: false,
  },
  {
    number: 4,
    lambda: 'skillsnap-job-router',
    description: 'Creates job record and user-job association with default generation types for each resume section.',
    tables: [
      { name: 'skillsnap-job', mode: 'write' },
      { name: 'skillsnap-user-job', mode: 'write' },
    ],
    services: [{ name: 'Lambda', color: 'gray' }],
    isAsync: false,
  },
  {
    number: 5,
    lambda: 'skillsnap-gentags (jobdesc)',
    description: 'Extracts tags from the job description using Bedrock Claude, creates vector embeddings with Bedrock Titan, and stores them for matching.',
    tables: [
      { name: 'skillsnap-job', mode: 'read' },
      { name: 'skillsnap-tag-embeddings', mode: 'write' },
    ],
    services: [{ name: 'Bedrock Claude', color: 'purple' }, { name: 'Bedrock Titan', color: 'purple' }, { name: 'Lambda', color: 'gray' }],
    isAsync: true,
  },
  {
    number: 6,
    lambda: 'SES',
    description: 'Sends acknowledgment email back to the user confirming the job was received and queued.',
    tables: [],
    services: [{ name: 'SES', color: 'orange' }],
    isAsync: true,
  },
];

const generateResumeSteps: StepData[] = [
  {
    number: 1,
    lambda: 'Frontend',
    description: 'User clicks Generate button. Sends POST /jobs/{jobid}/generate-all to API Gateway.',
    tables: [],
    services: [],
    isAsync: false,
  },
  {
    number: 2,
    lambda: 'skillsnap-generate-all',
    description: 'Validates the request, determines which components need generation, and queues one SQS message per component.',
    tables: [
      { name: 'skillsnap-user-job', mode: 'read/write' },
      { name: 'skillsnap-job', mode: 'read' },
    ],
    services: [{ name: 'SQS', color: 'orange' }, { name: 'Lambda', color: 'gray' }],
    isAsync: false,
  },
  {
    number: 3,
    lambda: 'SQS Queue',
    description: 'Delivers individual component messages to the subcomponent worker. Each message specifies the component type and generation mode.',
    tables: [],
    services: [{ name: 'SQS', color: 'orange' }],
    isAsync: true,
  },
  {
    number: 4,
    lambda: 'skillsnap-subcomponent',
    description: 'Processes each component based on its generation type. Embedding path: loads embeddings from tag-embeddings, scores items via tag_matcher.py, runs post-processors (PLI, PFF, PST), validates, stores YAML. Generate path: calls Bedrock Claude with section prompt, runs post-processors, fabrication check, stores YAML. Manual path: copies resume section as YAML.',
    tables: [
      { name: 'skillsnap-tag-embeddings', mode: 'read' },
      { name: 'skillsnap-resume', mode: 'read' },
      { name: 'skillsnap-user-job', mode: 'read/write' },
      { name: 'skillsnap-prompt', mode: 'read' },
      { name: 'skillsnap-ai-function', mode: 'read' },
    ],
    services: [
      { name: 'Bedrock Claude', color: 'purple' },
      { name: 'Lambda', color: 'gray' },
    ],
    isAsync: true,
  },
  {
    number: 5,
    lambda: 'Frontend (polling)',
    description: 'Frontend polls GET /jobs/{jobid} to detect when all subcomponents have completed generation.',
    tables: [],
    services: [],
    isAsync: true,
  },
  {
    number: 6,
    lambda: 'skillsnap-final-files',
    description: 'Reads all generated YAML subcomponents, renders complete HTML and PDF resume, uploads files to S3, and invalidates CloudFront cache.',
    tables: [
      { name: 'skillsnap-user-job', mode: 'read/write' },
      { name: 'skillsnap-resume', mode: 'read' },
      { name: 'skillsnap-user', mode: 'read' },
    ],
    services: [
      { name: 'S3', color: 'green' },
      { name: 'CloudFront', color: 'green' },
      { name: 'Lambda', color: 'gray' },
    ],
    isAsync: true,
  },
];

// --- Summary table data ---

const allTables = [
  { name: 'skillsnap-resume', flows: ['Resume Creation', 'Generate Resume'] },
  { name: 'skillsnap-user', flows: ['Resume Creation', 'Generate Resume'] },
  { name: 'skillsnap-tag-embeddings', flows: ['Resume Creation', 'Email Job', 'Generate Resume'] },
  { name: 'skillsnap-user-email', flows: ['Email Job'] },
  { name: 'skillsnap-job', flows: ['Email Job', 'Generate Resume'] },
  { name: 'skillsnap-user-job', flows: ['Email Job', 'Generate Resume'] },
  { name: 'skillsnap-prompt', flows: ['Generate Resume'] },
  { name: 'skillsnap-ai-function', flows: ['Generate Resume'] },
];

const allLambdas = [
  { name: 'skillsnap-resume-create', flows: ['Resume Creation'] },
  { name: 'skillsnap-gentags', flows: ['Resume Creation', 'Email Job'] },
  { name: 'skillsnap-genmasterresume', flows: ['Resume Creation'] },
  { name: 'skillsnap-job-router', flows: ['Email Job'] },
  { name: 'skillsnap-generate-all', flows: ['Generate Resume'] },
  { name: 'skillsnap-subcomponent', flows: ['Generate Resume'] },
  { name: 'skillsnap-final-files', flows: ['Generate Resume'] },
];

const allServices = [
  { name: 'DynamoDB', flows: ['Resume Creation', 'Email Job', 'Generate Resume'] },
  { name: 'S3', flows: ['Resume Creation', 'Email Job', 'Generate Resume'] },
  { name: 'SQS', flows: ['Generate Resume'] },
  { name: 'Bedrock Claude', flows: ['Resume Creation', 'Email Job', 'Generate Resume'] },
  { name: 'Bedrock Titan', flows: ['Resume Creation', 'Email Job'] },
  { name: 'CloudFront', flows: ['Resume Creation', 'Generate Resume'] },
  { name: 'SES', flows: ['Email Job'] },
  { name: 'API Gateway', flows: ['Resume Creation', 'Generate Resume'] },
];

// --- Main component ---

export function DataFlows() {
  return (
    <div className="p-6 max-w-5xl mx-auto">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-800">Data Flow Diagrams</h1>
        <p className="text-sm text-gray-500 mt-1">Interactive documentation of the three primary SkillSnap data flows. Click a section to expand.</p>
      </div>

      <ColorLegend />

      <div className="space-y-4 mb-8">
        <FlowSection
          title="1. Creating a New Resume"
          subtitle="User saves resume data through the editor, triggering tag extraction, embedding, and master resume generation."
          steps={resumeCreationSteps}
          defaultOpen={true}
        />
        <FlowSection
          title="2. Adding a Job from Email"
          subtitle="Inbound email is parsed by SES, routed to the correct parser, and stored as a new job with tag embeddings."
          steps={emailJobSteps}
        />
        <FlowSection
          title="3. Generating a Custom Resume for a Job"
          subtitle="User triggers generation, SQS fans out subcomponent work, Bedrock generates content, and final files are rendered."
          steps={generateResumeSteps}
        />
      </div>

      {/* Summary Table */}
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
        <div className="p-5 border-b border-gray-200">
          <h2 className="text-lg font-bold text-gray-800">Summary: Resources Across All Flows</h2>
        </div>
        <div className="overflow-x-auto">
          {/* DynamoDB Tables */}
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-gray-50">
                <th className="text-left px-4 py-2 font-semibold text-gray-700">DynamoDB Tables</th>
                <th className="text-left px-4 py-2 font-semibold text-gray-700">Used In</th>
              </tr>
            </thead>
            <tbody>
              {allTables.map((t, i) => (
                <tr key={i} className="border-t border-gray-100">
                  <td className="px-4 py-2"><code className="text-xs bg-blue-50 text-blue-800 px-1.5 py-0.5 rounded">{t.name}</code></td>
                  <td className="px-4 py-2 text-xs text-gray-600">{t.flows.join(', ')}</td>
                </tr>
              ))}
            </tbody>
          </table>

          {/* Lambda Functions */}
          <table className="w-full text-sm mt-2">
            <thead>
              <tr className="bg-gray-50">
                <th className="text-left px-4 py-2 font-semibold text-gray-700">Lambda Functions</th>
                <th className="text-left px-4 py-2 font-semibold text-gray-700">Used In</th>
              </tr>
            </thead>
            <tbody>
              {allLambdas.map((l, i) => (
                <tr key={i} className="border-t border-gray-100">
                  <td className="px-4 py-2"><code className="text-xs bg-gray-100 text-gray-800 px-1.5 py-0.5 rounded">{l.name}</code></td>
                  <td className="px-4 py-2 text-xs text-gray-600">{l.flows.join(', ')}</td>
                </tr>
              ))}
            </tbody>
          </table>

          {/* AWS Services */}
          <table className="w-full text-sm mt-2">
            <thead>
              <tr className="bg-gray-50">
                <th className="text-left px-4 py-2 font-semibold text-gray-700">AWS Services</th>
                <th className="text-left px-4 py-2 font-semibold text-gray-700">Used In</th>
              </tr>
            </thead>
            <tbody>
              {allServices.map((s, i) => (
                <tr key={i} className="border-t border-gray-100">
                  <td className="px-4 py-2 text-xs font-medium text-gray-800">{s.name}</td>
                  <td className="px-4 py-2 text-xs text-gray-600">{s.flows.join(', ')}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
